import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { ChartProps } from './types';
import { getSeriesColor } from './color-palette';
import { ChartPatternDefs } from './ChartPatternDefs';
import { ChartTooltip, TooltipData } from './ChartTooltip';

const MARGIN = { top: 20, right: 20, bottom: 60, left: 60 };
const WIDTH = 600;
const HEIGHT = 400;

/**
 * Velocity chart — bar chart with a trailing average overlay line.
 * Each bar = one period (sprint or week), average = dashed horizontal line.
 * Data format: dimensionValue = period label, measureValue = completed count.
 */
export function VelocityChart({ data, dimensionLabel, measureLabel, onSegmentClick }: ChartProps): React.ReactElement {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);

  const handleBarInteraction = useCallback(
    (dimensionValue: string, measureValue: number, event: React.MouseEvent | React.FocusEvent) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const target = event.currentTarget as HTMLElement;
      const targetRect = target.getBoundingClientRect();
      setTooltip({ dimensionValue, measureValue, dimensionLabel, measureLabel, x: targetRect.left - rect.left + targetRect.width / 2, y: targetRect.top - rect.top });
    },
    [dimensionLabel, measureLabel],
  );

  const hideTooltip = useCallback(() => setTooltip(null), []);

  // Compute trailing 3-period average
  const avgData = React.useMemo(() => {
    return data.map((d, i) => {
      if (i < 2) return null;
      const window = data.slice(i - 2, i + 1);
      const avg = window.reduce((sum, p) => sum + p.measureValue, 0) / window.length;
      return { dimensionValue: d.dimensionValue, avg: Math.round(avg * 10) / 10 };
    }).filter(Boolean) as { dimensionValue: string; avg: number }[];
  }, [data]);

  useEffect(() => {
    if (!svgRef.current || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('g.chart-content').remove();

    const innerWidth = WIDTH - MARGIN.left - MARGIN.right;
    const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom;

    const g = svg.append('g').attr('class', 'chart-content').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    const xScale = d3.scaleBand().domain(data.map((d) => d.dimensionValue)).range([0, innerWidth]).padding(0.3);
    const yMax = Math.max(d3.max(data, (d) => d.measureValue) ?? 0, d3.max(avgData, (d) => d.avg) ?? 0);
    const yScale = d3.scaleLinear().domain([0, yMax]).nice().range([innerHeight, 0]);

    // Gridlines
    g.append('g').call(d3.axisLeft(yScale).tickSize(-innerWidth).tickFormat(() => '')).selectAll('line').attr('stroke', '#e5e7eb').attr('stroke-dasharray', '2,2');
    g.select('.domain').remove();

    // Axes
    g.append('g').attr('transform', `translate(0,${innerHeight})`).call(d3.axisBottom(xScale)).selectAll('text').attr('transform', 'rotate(-30)').style('text-anchor', 'end').attr('font-size', '11px');
    g.append('g').call(d3.axisLeft(yScale)).attr('font-size', '12px');

    // Bars
    data.forEach((d) => {
      const color = getSeriesColor(d.dimensionValue);
      g.append('rect')
        .attr('x', xScale(d.dimensionValue) ?? 0)
        .attr('y', yScale(d.measureValue))
        .attr('width', xScale.bandwidth())
        .attr('height', innerHeight - yScale(d.measureValue))
        .attr('fill', color)
        .attr('rx', 2);
    });

    // Trailing average line (dashed)
    if (avgData.length > 0) {
      const avgLine = d3.line<{ dimensionValue: string; avg: number }>()
        .x((d) => (xScale(d.dimensionValue) ?? 0) + xScale.bandwidth() / 2)
        .y((d) => yScale(d.avg));

      g.append('path')
        .datum(avgData)
        .attr('fill', 'none')
        .attr('stroke', '#dc2626')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '6,3')
        .attr('d', avgLine);
    }

    // Axis labels
    g.append('text').attr('x', innerWidth / 2).attr('y', innerHeight + MARGIN.bottom - 5).attr('text-anchor', 'middle').attr('font-size', '13px').attr('fill', 'currentColor').text(dimensionLabel);
    g.append('text').attr('transform', 'rotate(-90)').attr('x', -innerHeight / 2).attr('y', -MARGIN.left + 15).attr('text-anchor', 'middle').attr('font-size', '13px').attr('fill', 'currentColor').text(measureLabel);
  }, [data, avgData, dimensionLabel, measureLabel]);

  if (data.length === 0) {
    return <div className="flex h-64 items-center justify-center text-muted-foreground" role="status">No data available</div>;
  }

  const innerWidth = WIDTH - MARGIN.left - MARGIN.right;
  const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const yMax = Math.max(d3.max(data, (d) => d.measureValue) ?? 0, d3.max(avgData, (d) => d.avg) ?? 0);
  const xScale = d3.scaleBand().domain(data.map((d) => d.dimensionValue)).range([0, innerWidth]).padding(0.3);
  const yScale = d3.scaleLinear().domain([0, yMax]).nice().range([innerHeight, 0]);

  return (
    <div ref={containerRef} className="relative" role="img" aria-label={`Velocity chart: ${dimensionLabel} by ${measureLabel}`}>
      <svg ref={svgRef} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="h-auto w-full max-w-[600px]" aria-hidden="true"><ChartPatternDefs /></svg>
      <div className="mt-2 flex items-center justify-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm bg-blue-600" />Completed</span>
        <span className="flex items-center gap-1"><span className="inline-block h-0.5 w-4 border-t-2 border-dashed border-red-600" />3-Period Avg</span>
      </div>
      <div className="absolute inset-0" aria-label="Chart data points" role="list">
        {data.map((d) => {
          const x = (MARGIN.left + (xScale(d.dimensionValue) ?? 0)) / WIDTH * 100;
          const y = (MARGIN.top + yScale(d.measureValue)) / HEIGHT * 100;
          const bw = xScale.bandwidth() / WIDTH * 100;
          const bh = (innerHeight - yScale(d.measureValue)) / HEIGHT * 100;
          return (
            <button key={d.dimensionValue} type="button" role="listitem" className="absolute border-0 bg-transparent p-0 outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2" style={{ left: `${x}%`, top: `${y}%`, width: `${bw}%`, height: `${bh}%` }} tabIndex={0} aria-label={`${dimensionLabel}: ${d.dimensionValue}, ${measureLabel}: ${d.measureValue}`} onMouseEnter={(e) => handleBarInteraction(d.dimensionValue, d.measureValue, e)} onFocus={(e) => handleBarInteraction(d.dimensionValue, d.measureValue, e)} onMouseLeave={hideTooltip} onBlur={hideTooltip} onClick={() => onSegmentClick?.(d.dimensionValue)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSegmentClick?.(d.dimensionValue); } }} />
          );
        })}
      </div>
      <ChartTooltip tooltip={tooltip} />
    </div>
  );
}
